import { useI18n } from '../../../i18n';
// ============================================
// PromotionDeleteModal Component
// ============================================

import { useEffect, useState } from 'react';

interface PromotionDeleteModalProps {
  isDeleting?: boolean;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}

export function PromotionDeleteModal({ isDeleting, onConfirm, onClose }: PromotionDeleteModalProps) {
  const { t } = useI18n();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const handleConfirm = async () => {
    setError(null);
    try {
      await onConfirm();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('failedToDeletePromotion'));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4">
          <h3 className="text-lg font-semibold text-slate-900">{t('deletePromotion')}</h3>
          <button onClick={onClose} className="rounded p-1 text-slate-400 hover:text-slate-600">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="border-t border-slate-100 px-6 py-5">
          <p className="text-sm text-slate-600">{t('areYouSureDeletePromotion') || 'Are you sure you want to delete this promotion?'}</p>
          {error && (
            <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-100 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800"
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={isDeleting}
            className="rounded-lg bg-red-500 px-5 py-2 text-sm font-semibold text-white hover:bg-red-600 disabled:opacity-60"
          >
            {isDeleting ? t('deleting') || 'Deleting...' : t('delete')}
          </button>
        </div>
      </div>
    </div>
  );
}
